import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import SecureStorage from "../utils/secureStorage";

interface IGenericProfile {
  user_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  birth_date?: string | null;
  country_code?: string | null;
  last_connection: string;
  avatar: string;
  is_active: boolean;
  creation_date_time: string | null;
  phone: string | null;
  user_account_status: string | null;
}

interface IGenericEntity {
  entity_name: string;
  entity_description: string;
  entity_identification: string;
  entity_status: string;
  entity_logo: string;
  accent_color?: string;
  owner_data?: {
    user_country_code?: string;
    user_identification?: string;
    first_name?: string;
    last_name?: string;
    avatar?: string;
  };
  google_places?: {
    google_place_id: string;
    review_link_enabled: boolean;
    formattedAddress: string;
    rating: number;
    displayName: string;
  };
}

type GenericEntityType = {
  process_data: any | null;
  entity_data: IGenericEntity | null;
  lmc_data?: any | null;
  business_card_form?: any | null;
};

type GenericAuthType = {
  isAuthenticated: boolean;
  user_identification: string | null;
  user_email?: string | null;
  user_role: string | null;
  user_last_connection: string | null;
  required_password_update: boolean | false;
  user_is_active: boolean | false;
  entity_identification?: string | null;
  lmcOptionRewardDetail?: { [key: string]: any } | null;
  profile: IGenericProfile;
  loggedInAt: string;
  access_token?: string;
  skip_gaming?: boolean;
};

export enum AuthActionKind {
  ENTITY = "ENTITY",
  LOGIN = "LOGIN",
  LMCDATA = "LMCDATA",
  BUSINESS_CARD_DATA = "BUSINESS_CARD_DATA",
  LOGOUT = "LOGOUT",
  SKIP_GAMING = "SKIP_GAMING",
}

export interface IGenericAuthContext {
  entity: GenericEntityType;
  auth: GenericAuthType;
}

export type AuthDispatchType = {
  type: AuthActionKind;
  payload: any;
};

const INITIAL_AUTH_STATE: GenericAuthType = {
  isAuthenticated: false,
  skip_gaming: false,
  profile: {
    avatar: "",
    email: "",
    is_active: false,
    last_connection: "",
    birth_date: null,
    country_code: null,
    first_name: null,
    last_name: null,
    user_name: null,
    creation_date_time: null,
    phone: null,
    user_account_status: null,
  },
  lmcOptionRewardDetail: null,
  required_password_update: false,
  user_identification: null,
  user_email: null,
  user_is_active: false,
  user_last_connection: null,
  user_role: null,
  loggedInAt: "",
};

const authReducer = (
  state: IGenericAuthContext,
  action: AuthDispatchType
): IGenericAuthContext => {
  const { type, payload } = action;

  switch (type) {
    case AuthActionKind.ENTITY:
      if (JSON.stringify(state.entity) === JSON.stringify(payload)) {
        return state;
      }
      return {
        ...state,
        entity: { ...payload },
        auth: {
          ...state.auth,
          skip_gaming: false,
        },
      };

    case AuthActionKind.LOGIN:
      const { user_password, password, ...sanitizedPayload } = payload as any;

      const authData = {
        isAuthenticated: true,
        loggedInAt: new Date().toISOString(),
        ...sanitizedPayload,
        skip_gaming: state.auth.skip_gaming,
      };

      try {
        localStorage.setItem("auth_state", JSON.stringify(authData));

        if (authData.user_identification && authData.access_token) {
          SecureStorage.updateAuthData({
            user_identification: authData.user_identification,
            access_token: authData.access_token,
            entity_identification: authData.entity_identification,
          });
        }
      } catch (e) {
        console.error("Failed to save auth state to localStorage:", e);
      }

      return {
        ...state,
        auth: authData,
      };

    case AuthActionKind.LMCDATA:
      return {
        ...state,
        auth: {
          ...state.auth,
          profile: { ...payload.user },
        },
        entity: {
          ...state.entity,
          lmc_data: { ...payload.data },
        },
      };

    case AuthActionKind.BUSINESS_CARD_DATA:
      return {
        ...state,
        entity: {
          ...state.entity,
          business_card_form: { ...payload.data },
        },
      };

    case AuthActionKind.LOGOUT:
      try {
        localStorage.removeItem("auth_state");

        SecureStorage.clearAuthData();
      } catch (e) {
        console.error("Failed to clear auth state from localStorage:", e);
      }

      return {
        ...state,
        auth: INITIAL_AUTH_STATE,
      };
    case AuthActionKind.SKIP_GAMING:
      return {
        ...state,
        auth: {
          ...state.auth,
          skip_gaming: true,
        },
      };

    default:
      return state;
  }
};

const isCustomerCardRoute = (): boolean => {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return (
    path.startsWith("/event/") ||
    path.startsWith("/loyalty-program/") ||
    path.startsWith("/business-card/") ||
    path.startsWith("/custom-registration/") ||
    path.startsWith("/process/")
  );
};

const getInitialState = (): IGenericAuthContext => {
  if (isCustomerCardRoute() && !SecureStorage.isImpersonating()) {
    SecureStorage.markCustomerTab();
  }

  let savedAuthState = null;
  try {
    const savedAuth = localStorage.getItem("auth_state");
    if (savedAuth) {
      const parsedAuth = JSON.parse(savedAuth);

      const { password, user_password, ...sanitized } = parsedAuth as any;
      savedAuthState = sanitized;

      if (
        savedAuthState.isAuthenticated &&
        savedAuthState.user_identification &&
        savedAuthState.access_token
      ) {
        const existingCredentials = SecureStorage.getAuthCredentials();
        if (!existingCredentials) {
          SecureStorage.updateAuthData({
            user_identification: savedAuthState.user_identification,
            access_token: savedAuthState.access_token,
            entity_identification: savedAuthState.entity_identification,
          });
          console.log(
            "Restored SecureStorage credentials from auth_state on page refresh"
          );
        }
      }
    }
  } catch (e) {
    console.error("Failed to restore auth state from localStorage:", e);
  }

  return {
    auth: savedAuthState || INITIAL_AUTH_STATE,
    entity: {
      entity_data: null,
      process_data: null,
      lmc_data: null,
      business_card_form: null,
    },
  };
};

const GenericAuthContext = createContext<{
  state: IGenericAuthContext;
  dispatch: React.Dispatch<AuthDispatchType>;
}>(null!);

export const GenericAuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(authReducer, getInitialState());

  return (
    <GenericAuthContext.Provider value={{ state, dispatch }}>
      {children}
    </GenericAuthContext.Provider>
  );
};

export const useGenericAuth = () => {
  const context = useContext(GenericAuthContext);
  if (!context) {
    throw new Error("useGenericAuth must be used within a GenericAuthProvider");
  }
  return context;
};

export const useLmcUserView = () => {
  const context = useGenericAuth();

  const dispatch = useCallback(
    (action: { type: any; payload: any }) => {
      let mappedAction = action;
      if (action.type === "LOGIN") {
        mappedAction = { ...action, type: AuthActionKind.LOGIN };
      } else if (action.type === "ENTITY") {
        mappedAction = { ...action, type: AuthActionKind.ENTITY };
      } else if (action.type === "LMCDATA") {
        mappedAction = { ...action, type: AuthActionKind.LMCDATA };
      } else if (action.type === "LOGOUT") {
        mappedAction = { ...action, type: AuthActionKind.LOGOUT };
      } else if (action.type === "SKIP_GAMING") {
        mappedAction = { ...action, type: AuthActionKind.SKIP_GAMING };
      }

      context.dispatch(mappedAction);
    },
    [context.dispatch]
  );

  return {
    state: {
      entity: {
        process_data: context.state.entity.process_data,
        entity_data: context.state.entity.entity_data,
        lmc_data: context.state.entity.lmc_data,
      },
      auth: context.state.auth,
    },
    dispatch,
  };
};

export const useBusinessCardAuth = () => {
  const context = useGenericAuth();

  return {
    state: {
      entity: {
        process_data: context.state.entity.process_data,
        entity_data: context.state.entity.entity_data,
        business_card_form: context.state.entity.business_card_form,
      },
      auth: context.state.auth,
    },
    dispatch: context.dispatch,
  };
};

export default GenericAuthProvider;
